import React from "react";
import {
  Table,
  TableCell,
  TableBody,
  TableHead,
  TableRow,
  CheckboxField,
} from "@aws-amplify/ui-react";
import { I18n } from "aws-amplify";
import { AiOutlineDownload, AiOutlinePrinter } from "react-icons/ai";
import { BsArrowUpShort, BsArrowDownShort } from "react-icons/bs";

const SearchResults = ({
  results,
  downloadFile,
  downloadZip,
  printPDF,
  printMergedPDFs,
  selectedFiles,
  setSelectedFiles,
  onSortChange,
  sortCriteria,
}) => {
  const fileSelectLimit = 100;

  const handlePrintSelectedPDFs = async () => {
    const pdfFileNames = selectedFiles.map((file) => file.pdf);
    await printMergedPDFs(pdfFileNames);
  };

  const parseAdditionalFiles = (fileString) => {
    if (!fileString) return [];

    const files = fileString.split(";").filter(Boolean);
    return files
      .map((file) => {
        const fileName = file.split(":")[1];
        return fileName ? `AdditionalFiles/${fileName}` : null;
      })
      .filter(Boolean);
  };

  // handles different file downloads
  const handleDownload = async (e, result) => {
    const isTestEnv = process.env.REACT_APP_TEST === "true";
    switch (e.target.value) {
      case "pdf":
        const pdfFileName = isTestEnv
          ? "pdfFile-test.pdf"
          : result._source.PDFFileName;
        downloadFile(`PDFFiles/${pdfFileName}`);
        break;
      case "report":
        const reportFileName = isTestEnv
          ? "signatureReport-test.html"
          : result._source.SignatureReportName;
        downloadFile(`SignatureReports/${reportFileName}`);
        break;
      case "printPdf":
        printPDF(result._source.PDFFileName);
        break;
      case "additional":
        if (isTestEnv) {
          downloadFile("AdditionalFiles/additionalFile-test2.edi");
          downloadFile("AdditionalFiles/additionalFile-test1.csv");
        } else {
          const additionalFiles = parseAdditionalFiles(
            result._source.AdditionalFiles
          );
          additionalFiles.forEach((file) => downloadFile(file));
        }
        break;
      default:
        console.warn("Unknown option selected");
    }
    e.target.value = "default";
  };

  function formatContentType(contentType) {
    const language = getBrowserLanguage();

    if (language.startsWith("de")) {
      return contentType.replace("invoice", "Rechnungen");
    } else {
      return contentType.replace("Rechnungen", "Invoice");
    }
  }

  const getStateTypeDisplay = (documentState) => {
    switch (documentState) {
      case "1":
        return I18n.get("New");
      case "2":
        return I18n.get("Read");
      case "3":
        return I18n.get("Downloaded");
      default:
        return documentState;
    }
  };

  function getBrowserLanguage() {
    return navigator.language || navigator.userLanguage;
  }

  //display different date format based on language
  function formatDate(originalDateFormat) {
    const datePart = originalDateFormat.split("T")[0];
    const [year, month, day] = datePart.split("-");

    const language = getBrowserLanguage();

    if (language.startsWith("de")) {
      return `${day}.${month}.${year}`; //German format
    } else {
      return `${month}/${day}/${year}`; //default English format
    }
  }

  //display different icons based on sort asc or desc
  const renderSortIcon = (columnName) => {
    if (sortCriteria.column === columnName) {
      return sortCriteria.order === "asc" ? (
        <BsArrowUpShort className="up-icon" />
      ) : (
        <BsArrowDownShort className="down-icon" />
      );
    }
    return null;
  };

  const SortableHeader = ({ columnName, label }) => (
    <TableCell
      as="th"
      className="table-title"
      onClick={() => onSortChange(columnName)}
    >
      {I18n.get(label)} {renderSortIcon(columnName)}
    </TableCell>
  );

  return (
    <Table className="result-table" variation="striped" size="small">
      <TableHead>
        <TableRow>
          <TableCell as="th" colSpan="1">
            <AiOutlineDownload
              className="download-icon"
              onClick={downloadZip}
            />
            <AiOutlinePrinter
              className="download-icon"
              onClick={handlePrintSelectedPDFs}>
            </AiOutlinePrinter>
          </TableCell>
          <TableCell as="th">{I18n.get("Type")}</TableCell>
          <SortableHeader columnName="PDFFileName" label="Name" />
          <SortableHeader columnName="ViewStatus" label="Document state" />
          <SortableHeader columnName="ContentType" label="Document type" />
          <SortableHeader columnName="CN" label="Customer Number" />
          <SortableHeader columnName="DOC" label="Document Originator Code" />
          <SortableHeader
            columnName="DocumentOriginatorName"
            label="Document Originator Name"
          />
          <SortableHeader columnName="Doc_DAT_E" label="Document Date" />
          <SortableHeader
            columnName="FiscalInvoiceNumber"
            label="Fiscal Invoice Number"
          />
          <SortableHeader columnName="Doc_No" label="Document Number" />
          <SortableHeader
            columnName="OriginatorCountry"
            label="Originator Country"
          />
          <SortableHeader
            columnName="ReceivingCountry"
            label="Receiving Country"
          />
          <SortableHeader columnName="DocumentAmount" label="Document Amount" />
          <SortableHeader columnName="Currency" label="Currency" />
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((result) => (
          <TableRow className="result-items" key={result._id}>
            <TableCell>
            <div
                onClick={() => {
                  if (
                    selectedFiles.length >= fileSelectLimit &&
                    !selectedFiles.some(
                      (file) => file.pdf === result._source.PDFFileName
                    )
                  ) {
                    window.alert(
                      I18n.get( "You cannot select more than ") +
                        fileSelectLimit +
                        I18n.get( " files!")
                    );
                  }
                }}
              >
                <CheckboxField
                  name="subscribe"
                  value={JSON.stringify({
                    pdf: result._source.PDFFileName,
                    report: result._source.SignatureReportName,
                    additional: result._source.AdditionalFiles,
                  })}
                  checked={selectedFiles.some(
                    (file) => file.pdf === result._source.PDFFileName
                  )}
                  disabled={
                    selectedFiles.length >= fileSelectLimit &&
                    !selectedFiles.some(
                      (file) => file.pdf === result._source.PDFFileName
                    )
                  }
                  onChange={(e) => {
                    const fileData = JSON.parse(e.target.value);
                    if (e.target.checked) {
                      setSelectedFiles((prevFiles) => [...prevFiles, fileData]);
                    } else {
                      setSelectedFiles((prevFiles) => 
                        prevFiles.filter((file) => file.pdf !== fileData.pdf)
                      );
                    }
                  }}
                />
              </div>
            </TableCell>
            <TableCell>
              <select
                onChange={(e) => handleDownload(e, result)}
                defaultValue="default"
              >
                <option value="default" disabled>
                  {I18n.get("Select file type")}
                </option>
                <option value="pdf">PDF</option>
                <option value="report">
                  {I18n.get("Verification report")}
                </option>
                <option value="printPdf">{I18n.get("Print")}</option>
                {result._source.AdditionalFiles && (
                  <option value="additional">
                    {I18n.get("Additional Files")}
                  </option>
                )}
              </select>
            </TableCell>
            <TableCell>
              {result._source.PDFFileName.replace(".pdf", "")}
            </TableCell>
            <TableCell>
              {getStateTypeDisplay(result._source.ViewStatus)}
            </TableCell>
            <TableCell>
              {formatContentType(result._source.ContentType)}
            </TableCell>
            <TableCell>{result._source.CN}</TableCell>
            <TableCell>{result._source.DOC}</TableCell>
            <TableCell>{result._source.DocumentOriginatorName}</TableCell>
            <TableCell>{formatDate(result._source.Doc_DAT_E)}</TableCell>
            <TableCell>{result._source.FiscalInvoiceNumber}</TableCell>
            <TableCell>{result._source.Doc_No}</TableCell>
            <TableCell>{result._source.OriginatorCountry}</TableCell>
            <TableCell>{result._source.ReceivingCountry}</TableCell>
            <TableCell>{result._source.DocumentAmount}</TableCell>
            <TableCell>{result._source.Currency}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default SearchResults;
